import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAdminAuth } from '../../store/slices/adminAuthSlice';
import {
  fetchUsersThunk,
  setFilters,
  setPage,
  selectUsers,
  selectUserMeta,
  selectUserFilters,
  selectUserPage,
  selectUserLoading,
  selectUserError,
  updateUserApiKeyStatus
} from '../../store/slices/userManagementSlice';
import { toggleApiKeyStatus, createCustomUser } from '../../api/admin';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Button, Stack, CircularProgress, Alert, useMediaQuery, Skeleton, Chip, Tooltip, IconButton, Divider, Grid, Tabs, Tab, Avatar, InputAdornment,
  MenuItem,
  FormControlLabel,
  Switch,
  DialogActions,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { toast } from 'react-toastify';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';

import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import * as Yup from 'yup'; 
import { CustomUserSchema } from '../../validations/customUserSchema';

const ITEMS_PER_PAGE = 20;

function SkeletonTableRows({ rows = 8, columns = 10 }) {
  return Array.from({ length: rows }).map((_, i) => (
    <TableRow key={i}>
      {Array.from({ length: columns }).map((_, j) => (
        <TableCell key={j}><Skeleton variant="rectangular" height={24} /></TableCell>
      ))}
    </TableRow>
  ));
}

function CustomUserForm({ onSave, onCancel, isSaving }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'user',
    apiQuota: 100,
    durationDays: 7,
    verifyDirectly: true
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validate = async () => {
    try {
      await CustomUserSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      const newErrors = {};
      err.inner.forEach(error => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (await validate()) {
      const payload = {
        ...formData,
        apiQuota: Number(formData.apiQuota),
        durationDays: Number(formData.durationDays)
      };
      onSave(payload);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? '' : Number(value) // Allow empty string for better UX
    }));
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Alert severity="info" sx={{ mb: 3 }}>
        This user will receive <strong>{formData.apiQuota} API requests</strong> valid for{' '}
        <strong>{formData.durationDays} days</strong>
        {formData.verifyDirectly && ' with email pre-verified'}
      </Alert>

      <Stack spacing={3}>
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
          Personal Information
        </Typography>
        
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            error={!!errors.firstName}
            helperText={errors.firstName}
            required
          />
          <TextField
            fullWidth
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            error={!!errors.lastName}
            helperText={errors.lastName}
            required
          />
        </Stack>

        <TextField
          fullWidth
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          error={!!errors.email}
          helperText={errors.email}
          required
        />

        <TextField
          fullWidth
          label="Password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange}
          error={!!errors.password}
          helperText={errors.password}
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <TextField
          fullWidth
          label="Confirm Password"
          name="confirmPassword"
          type={showConfirmPassword ? 'text' : 'password'}
          value={formData.confirmPassword}
          onChange={handleChange}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <TextField
          fullWidth
          select
          label="Role"
          name="role"
          value={formData.role}
          onChange={handleChange}
          error={!!errors.role}
          helperText={errors.role}
          required
          readOnly
        >
          <MenuItem value="user">User</MenuItem>
        </TextField>

        <Divider />

        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
          API Access Configuration
        </Typography>

        <TextField
          fullWidth
          label="API Quota (Requests)"
          name="apiQuota"
          type="number"
          value={formData.apiQuota}
          onChange={handleChange}
          error={!!errors.apiQuota}
          helperText={errors.apiQuota || 'Number of API requests allowed'}
          required
          inputProps={{ min: 1, max: 1000000 }}
        />

        <TextField
          fullWidth
          label="Duration (Days)"
          name="durationDays"
          type="number"
          value={formData.durationDays}
          onChange={handleChange}
          error={!!errors.durationDays}
          helperText={errors.durationDays || 'Number of days quota is valid'}
          required
          inputProps={{ min: 1, max: 365 }}
        />

        {/* <FormControlLabel
          control={
            <Switch
              checked={formData.verifyDirectly}
              onChange={handleChange}
              name="verifyDirectly"
              color="primary"
            />
          }
          label="Verify email directly (skip verification email)"
        /> */}
      </Stack>

      <DialogActions sx={{ mt: 3, px: 0 }}>
        <Button onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={18} /> : <PersonAddIcon />}
        >
          {isSaving ? 'Creating User...' : 'Create Custom User'}
        </Button>
      </DialogActions>
    </Box>
  );
}

const UserManagement = () => {
  const dispatch = useDispatch();
  const { token: adminToken } = useSelector(selectAdminAuth);
  const users = useSelector(selectUsers);
  const meta = useSelector(selectUserMeta);
  const filters = useSelector(selectUserFilters);
  const page = useSelector(selectUserPage);
  const loading = useSelector(selectUserLoading);
  const error = useSelector(selectUserError);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchFields, setSearchFields] = useState({ search: '' });
  const [revokeLoading, setRevokeLoading] = useState(null);
  const [detailsUser, setDetailsUser] = useState(null);
  const [filteredUsers, setFilteredUsers] = useState(users);

  const [openCreate, setOpenCreate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (adminToken && users.length === 0 && !loading) {
      dispatch(fetchUsersThunk({ filters, page }));
    }
  }, [adminToken]);

  useEffect(() => {
    if (searchFields.search) {
      setFilteredUsers(
        users.filter((user) =>
            user.email.toLowerCase().includes(searchFields.search.toLowerCase()) ||
            user.first_name?.toLowerCase().includes(searchFields.search.toLowerCase()) ||
            user.last_name?.toLowerCase().includes(searchFields.search.toLowerCase())
        )
      );
    } else {
      setFilteredUsers(users);
    }
  }, [users, searchFields]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setSearchFields({ search: value });
  };

  const handlePageChange = (newPage) => {
    dispatch(setPage(newPage));
    dispatch(fetchUsersThunk({ filters, page: newPage }));
  };

  const handleCopyApiKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('API Key copied!');
  };

  const handleToggleApiKey = async (userId, apiKeyId, enable) => {
    setRevokeLoading(userId);
    try {
      await toggleApiKeyStatus(adminToken, apiKeyId, enable);
      toast.success(`API Key ${enable ? 'enabled' : 'disabled'}!`);
      dispatch(updateUserApiKeyStatus({ userId, isActive: enable }));
      // No need to refetch all users
    } catch (err) {
      toast.error(err.message || 'Failed to update API key status');
    } finally {
      setRevokeLoading(null);
    }
  };

  const handleCreateUser = async (formData) => {
  setIsSaving(true);
  try {
    const payload = {
      email: formData.email,
      password: formData.password,
      first_name: formData.firstName,
      last_name: formData.lastName,
      role: formData.role,
      api_quota: formData.apiQuota,
      duration_days: formData.durationDays,
      verify_directly: true,
    };

    await createCustomUser(payload, adminToken);
    toast.success('Custom user created successfully!');
    setOpenCreate(false);
    dispatch(fetchUsersThunk({ filters, page }));
  } catch (err) {
    const errorMsg = err?.response?.data?.error || err?.response?.data?.message || 'Failed to create custom user';
    toast.error(errorMsg);
  } finally {
    setIsSaving(false);
  }
};

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        User Management
      </Typography>

      <Paper sx={{ p: isMobile ? 1 : 2, mb: 3, overflowX: isMobile ? 'auto' : 'visible' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1.5 : 2,
            alignItems: isMobile ? 'stretch' : 'center',
            flexWrap: isMobile ? 'nowrap' : 'wrap',
            overflowX: isMobile ? 'auto' : 'visible',
            pb: isMobile ? 1 : 0,
            minWidth: isMobile ? 600 : 'unset',
          }}
        >
          <TextField label="Search by user name or email" name="search" value={searchFields.search} onChange={handleFieldChange} size="small" fullWidth={isMobile} sx={{ minWidth: 350 }} />
          <Tooltip title="Refresh">
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={() => dispatch(fetchUsersThunk({ filters, page }))}
                sx={{ mt: isMobile ? 1 : 0, borderRadius: 1, minWidth: 120, fontWeight: 'bold', boxShadow: 2 }}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                disabled={loading}
              >
                Refresh
              </Button>
            </span>
          </Tooltip>
          <Button
              variant="contained"
              color="secondary"
              sx={{ mt: isMobile ? 1 : 0, borderRadius: 1, minWidth: 160, fontWeight: 'bold', boxShadow: 2,  ml: 'auto' }}
              onClick={() => setOpenCreate(true)}
              startIcon={<PersonAddIcon />}
            >
              Add Custom User
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Email</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Subscription Status</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Plan</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Interval</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>API Key</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Disabled by Admin</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Units Granted</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Units Consumed</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>All Time Units</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <SkeletonTableRows rows={Math.max(2, Math.min(users.length || 1, 8))} columns={10} />
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Email</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Subscription Status</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Plan</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Interval</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>API Key</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Disabled by Admin</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Units Granted</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Units Consumed</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>All Time Units</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">No users found.</TableCell>
                </TableRow>
              ) : (
                filteredUsers.map(user => (
                  <TableRow key={user.id}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {user.subscription?.status === 'active' ? (
                        <Stack direction="row" spacing={0.25} alignItems="center">
                          <Chip label="Active" color="success" size="small" sx={{ fontSize: 10, height: 22, px: 0.75 }} />
                          {typeof user.subscription.is_custom !== 'undefined' && (
                            <Chip
                              label={user.subscription.is_custom ? 'Custom Plan' : 'Standard Plan'}
                              color={user.subscription.is_custom ? 'warning' : 'info'}
                              size="small"
                              sx={{ fontSize: 10, height: 22, px: 0.75 }}
                            />
                          )}
                        </Stack>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{user.subscription?.plan_name || '-'}</TableCell>
                    <TableCell>
                      {user.subscription?.plan_interval || '-'}
                    </TableCell>
                    <TableCell>
                      {user.api_key?.key ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Tooltip title="Copy API Key"><IconButton size="small" onClick={() => handleCopyApiKey(user.api_key.key)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                          <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{user.api_key.key.slice(0, 8)}...{user.api_key.key.slice(-4)}</span>
                        </Stack>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {user.api_key?.key ? (
                        <Tooltip title={!user.api_key?.disabled_by_admin ? 'Disable API Key' : 'Enable API Key'}>
                          <span>
                            <IconButton
                              size="small"
                              color={!user.api_key?.disabled_by_admin ? 'error' : 'success'}
                              disabled={!user.api_key?.key || revokeLoading === user.id}
                              onClick={() => user.api_key?.key && handleToggleApiKey(user.id, user.api_key.id, !user.api_key.disabled_by_admin)}
                            >
                              {revokeLoading === user.id ? <CircularProgress size={18} /> : (!user.api_key?.disabled_by_admin ? <ToggleOnIcon /> : <ToggleOffIcon />)}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : '-'}
                    </TableCell>
                    <TableCell>{user.api_key?.units_granted ?? '-'}</TableCell>
                    <TableCell>{user.api_key?.current_cycle_requests ?? '-'}</TableCell>
                    <TableCell>{user.total_usage ?? '-'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip title="Show Details">
                          <span>
                            <IconButton size="small" color="primary" onClick={() => setDetailsUser(user)}>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={!!detailsUser} onClose={() => setDetailsUser(null)} maxWidth="lg" fullWidth
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center',
          },
          '& .MuiPaper-root': {
          },
        }}
      >
        <DialogTitle sx={{ p: 0, position: 'relative' }}>
          {detailsUser && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              bgcolor: 'primary.main',
              color: '#fff',
              px: 4,
              py: 3,
              position: 'relative',
            }}>
              <Avatar
                src={detailsUser.photo_url}
                sx={{ width: 64, height: 64, fontSize: 32, fontWeight: 700, bgcolor: 'secondary.main', mr: 2 }}
              >
                {!detailsUser.photo_url && (detailsUser.first_name?.[0] || detailsUser.email?.[0] || 'U')}
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#fff' }}>
                  {detailsUser.first_name || detailsUser.last_name
                    ? `${detailsUser.first_name || ''} ${detailsUser.last_name || ''}`.trim()
                    : detailsUser.email}
                </Typography>
                <Typography variant="body2" sx={{ color: '#e3e3e3' }}>{detailsUser.email}</Typography>
                <Stack direction="row" spacing={1} mt={1}>
                  <Chip label={detailsUser.role || 'User'} color="secondary" size="small" />
                  <Chip
                    label={detailsUser.is_active ? 'Active' : 'Inactive'}
                    color={detailsUser.is_active ? 'success' : 'default'}
                    size="small"
                  />
                  {detailsUser.email_verified && <Chip label="Email Verified" color="success" size="small" />}
                </Stack>
              </Box>
              <IconButton
                aria-label="close"
                onClick={() => setDetailsUser(null)}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: 16,
                  color: '#fff',
                  bgcolor: 'rgba(0,0,0,0.08)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.18)' },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {detailsUser && (
            <>
              <Tabs
                value={detailsUser.tabIndex || 0}
                onChange={(e, v) => setDetailsUser({ ...detailsUser, tabIndex: v })}
                sx={{ mb: 1 }}
                textColor="primary"
                indicatorColor="primary"
              >
                <Tab label="User Info" />
                <Tab label="Subscription" />
                <Tab label="API Key" />
              </Tabs>
              {(!detailsUser.tabIndex || detailsUser.tabIndex === 0) && (
                <Paper elevation={3} sx={{ p: { xs: 3, sm: 5 }, mt: 0, mb: 4, borderRadius: 3, boxShadow: 4 }}>
                  <Stack spacing={3}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                      <Box flex={1}>
                        <Typography variant="subtitle2" color="text.secondary">First Name</Typography>
                        <Typography variant="h6" fontWeight={700}>{detailsUser.first_name || '-'}</Typography>
                      </Box>
                      <Box flex={1}>
                        <Typography variant="subtitle2" color="text.secondary">Last Name</Typography>
                        <Typography variant="h6" fontWeight={700}>{detailsUser.last_name || '-'}</Typography>
                      </Box>
                    </Stack>
                    <Divider />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                      <Box flex={1}>
                        <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                        <Typography variant="h6" fontWeight={700}>{detailsUser.email}</Typography>
                      </Box>
                      <Box flex={1} display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle2" color="text.secondary">Email Verified</Typography>
                        <Chip label={detailsUser.email_verified ? 'Yes' : 'No'} color={detailsUser.email_verified ? 'success' : 'default'} size="small" />
                      </Box>
                    </Stack>
                    <Divider />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                      <Box flex={1}>
                        <Typography variant="subtitle2" color="text.secondary">Stripe Customer ID</Typography>
                        <Typography variant="h6" fontWeight={700}>{detailsUser.stripe_customer_id || '-'}</Typography>
                      </Box>
                      <Box flex={1}>
                        <Typography variant="subtitle2" color="text.secondary">Current Subscription ID</Typography>
                        <Typography variant="h6" fontWeight={700}>{detailsUser.current_subscription_id || '-'}</Typography>
                      </Box>
                    </Stack>
                  </Stack>
                </Paper>
              )}
              {detailsUser.tabIndex === 1 && (
                <Paper elevation={3} sx={{ p: { xs: 3, sm: 5 }, mt: 0, mb: 4, borderRadius: 3, boxShadow: 4 }}>
                  {detailsUser.subscription ? (
                    <Stack spacing={3}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                        <Box flex={1}>
                          <Typography variant="subtitle2" color="text.secondary">Plan Name</Typography>
                          <Typography variant="h6" fontWeight={700}>{detailsUser.subscription.plan_name}</Typography>
                          {typeof detailsUser.subscription.is_custom !== 'undefined' && (
                            <Chip
                              label={detailsUser.subscription.is_custom ? 'Custom Plan' : 'Standard Plan'}
                              color={detailsUser.subscription.is_custom ? 'warning' : 'info'}
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          )}
                        </Box>
                        <Box flex={1} display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                          <Chip label={detailsUser.subscription.status} color={detailsUser.subscription.status === 'active' ? 'success' : 'default'} size="small" />
                        </Box>
                      </Stack>
                      <Divider />
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                        <Box flex={1}>
                          <Typography variant="subtitle2" color="text.secondary">Price</Typography>
                          <Typography variant="h6" fontWeight={700}>{detailsUser.subscription.price}</Typography>
                        </Box>
                        <Box flex={1}>
                          <Typography variant="subtitle2" color="text.secondary">Interval</Typography>
                          <Typography variant="h6" fontWeight={700}>{detailsUser.subscription.plan_interval}</Typography>
                        </Box>
                      </Stack>
                      <Divider />
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                        <Box flex={1}>
                          <Typography variant="subtitle2" color="text.secondary">Start Date</Typography>
                          <Typography variant="body1" fontWeight={500}>{detailsUser.subscription.start_date ? new Date(detailsUser.subscription.start_date).toLocaleString() : '-'}</Typography>
                        </Box>
                        <Box flex={1}>
                          <Typography variant="subtitle2" color="text.secondary">End Date</Typography>
                          <Typography variant="body1" fontWeight={500}>{detailsUser.subscription.end_date ? new Date(detailsUser.subscription.end_date).toLocaleString() : '-'}</Typography>
                        </Box>
                      </Stack>
                      <Divider />
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                        <Box flex={1}>
                          <Typography variant="subtitle2" color="text.secondary">Stripe Sub ID</Typography>
                          <Typography variant="body1" fontWeight={500}>{detailsUser.subscription.stripe_sub_id || '-'}</Typography>
                        </Box>
                        <Box flex={1}>
                          <Typography variant="subtitle2" color="text.secondary">Currency</Typography>
                          <Typography variant="body1" fontWeight={500}>{detailsUser.subscription.plan_currency}</Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">No subscription</Typography>
                  )}
                </Paper>
              )}
              {detailsUser.tabIndex === 2 && (
                <Paper elevation={3} sx={{ p: { xs: 3, sm: 5 }, mt: 0, mb: 2, borderRadius: 3, boxShadow: 4 }}>
                  {detailsUser.api_key ? (
                    <Stack spacing={3}>
                      {/* API Key section */}
                      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={8}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 80 }}>API Key</Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ fontFamily: 'monospace', fontSize: 18 }}>
                          {detailsUser.api_key.key.slice(0, 8)}...{detailsUser.api_key.key.slice(-4)}
                        </Typography>
                        <Tooltip title="Copy API Key">
                          <IconButton size="small" onClick={() => {navigator.clipboard.writeText(detailsUser.api_key.key)}}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Chip
                          label={detailsUser.api_key?.is_active ? 'Active' : 'Disabled'}
                          color={detailsUser.api_key?.is_active ? 'success' : 'error'}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Stack>
                      {/* Add disabled_by_admin display */}
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="subtitle2" color="text.secondary">Disabled by Admin:</Typography>
                        <Chip
                          label={detailsUser.api_key.disabled_by_admin ? 'Yes' : 'No'}
                          color={detailsUser.api_key.disabled_by_admin ? 'error' : 'success'}
                          size="small"
                        />
                      </Stack>
                      <Divider sx={{ my: 1 }} />
                      {/* Units row */}
                      <Stack direction="row" spacing={15} justifyContent="flex-start" alignItems="center">
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Units Granted</Typography>
                          <Typography variant="h6" fontWeight={700}>{detailsUser.api_key.units_granted}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Units Consumed</Typography>
                          <Typography variant="h6" fontWeight={700}>{detailsUser.api_key.current_cycle_requests}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Total Usage</Typography>
                          <Typography variant="h6" fontWeight={700}>{detailsUser.total_usage ?? '-'}</Typography>
                        </Box>
                      </Stack>
                      <Divider sx={{ my: 1 }} />
                      {/* Dates row */}
                      <Stack direction="row" spacing={25}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Created At</Typography>
                          <Typography variant="body2" color="text.secondary">{detailsUser.api_key.created_at ? new Date(detailsUser.api_key.created_at).toLocaleString() : '-'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Expires At</Typography>
                          <Typography variant="body2" color="text.secondary">{detailsUser.api_key.expires_at ? new Date(detailsUser.api_key.expires_at).toLocaleString() : '-'}</Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">No API key</Typography>
                  )}
                </Paper>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center" mt={2}>
        <Button
          variant="outlined"
          disabled={page === 0}
          onClick={() => handlePageChange(page - 1)}
        >
          Previous
        </Button>
        <Typography>
          Page {page + 1} {meta.total ? `/ ${Math.ceil(meta.total / ITEMS_PER_PAGE)}` : ''}
        </Typography>
        <Button
          variant="outlined"
          disabled={meta.total ? (page + 1) * ITEMS_PER_PAGE >= meta.total : true}
          onClick={() => handlePageChange(page + 1)}
        >
          Next
        </Button>
      </Stack>
      {/* Create Custom User Dialog */}
      <Dialog 
        open={openCreate} 
        onClose={() => !isSaving && setOpenCreate(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Create Custom User
        </DialogTitle>
        <DialogContent>
          <CustomUserForm 
            onSave={handleCreateUser}
            onCancel={() => setOpenCreate(false)}
            isSaving={isSaving}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UserManagement; 